import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

function StatusBadge({ status }) {
  const styles = status === 'open'
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
      {status === 'open' ? 'Open' : 'Closed'}
    </span>
  );
}

export default function SuperadminSupportTicketsIndex({ tickets = [], filters = {}, counts = { open: 0, closed: 0 } }) {
  const [query, setQuery] = useState(filters.q || '');
  const active = filters.status || 'open';

  const navigate = (next) => {
    const params = { ...next };
    if (!params.q) delete params.q;
    if (params.status === 'open') delete params.status;
    router.get('/superadmin/support_tickets', params, { preserveState: true, preserveScroll: true });
  };

  const setFilter = (key) => {
    navigate({ status: key, q: query });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate({ status: active, q: query });
  };

  const handleClear = () => {
    setQuery('');
    navigate({ status: active });
  };

  const total = tickets.length;
  const hasFilter = (filters.q || '').trim().length > 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Support Inbox</h1>
            <p className="mt-1 text-sm text-gray-500">Customer support tickets across the portal.</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by subject or user email…"
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition cursor-pointer"
              >
                Search
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </form>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            {FILTERS.map((f) => {
              const isActive = active === f.key;
              const count = f.key === 'open' ? counts.open : f.key === 'closed' ? counts.closed : counts.open + counts.closed;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border transition cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                  <span className={`inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 text-xs rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {total === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-600">
                {hasFilter ? 'No tickets match that search.' : active === 'closed' ? 'No closed tickets.' : active === 'all' ? 'No tickets yet.' : 'No open tickets.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {tickets.map((t) => (
                <Link
                  key={t.id}
                  href={`/superadmin/support_tickets/${t.id}`}
                  className="block py-4 first:pt-0 last:pb-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 truncate">{t.subject}</p>
                        <StatusBadge status={t.status} />
                        {t.awaiting_staff_reply && t.status === 'open' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-800 border-amber-200">
                            Needs reply
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-medium text-gray-700">{t.user.name}</span> · {t.user.email}
                      </p>
                      {t.last_message_preview && (
                        <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">
                          <span className="text-xs font-semibold text-gray-500">
                            {t.last_message_from_staff ? 'Staff: ' : 'User: '}
                          </span>
                          {t.last_message_preview}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1.5">
                        {t.message_count} {t.message_count === 1 ? 'message' : 'messages'}
                        {t.last_activity_label && <span> · {t.last_activity_label}</span>}
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-300 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
